import Image from 'next/image';

import { Box, Heading, LinkOverlay, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { PromoCard } from '~/features/experiments/ab_plp_redesign_2025/components/promo-card';
import { RocketIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import {
  type Summer2026SaleVariant,
  getSummer2026SaleCheckoutHref,
  SUMMER_2026_SALE_CTA_LABEL,
  SUMMER_2026_SALE_VARIANTS,
} from '~/features/promotions/constants/summer-2026-sale';

import { EndsSoonEyebrow } from './ends-soon-eyebrow';

/**
 * Homepage sitewide sale card — a thin wrapper around the shared `PromoCard` with variant-specific background art and `EndsSoonEyebrow`.
 */
export function SitewideSalePromoCard({
  variant,
  promoCode,
  imagePriority,
  onClick,
}: {
  variant: Summer2026SaleVariant;
  /** Live Braavos coupon from the promotion response; appended to the CTA as `?coupon=`. */
  promoCode?: string | null;
  imagePriority?: boolean;
  onClick?: () => void;
}) {
  const config = SUMMER_2026_SALE_VARIANTS[variant];
  const checkoutHref = getSummer2026SaleCheckoutHref(variant, promoCode);
  const cardId = `${variant}-sale-title`;

  const backgroundImage = (
    <Image
      alt=''
      loading='eager'
      priority={imagePriority}
      sizes='(max-width: 768px) 100vw, 768px'
      src={config.homepageBg}
      style={{ objectFit: config.homepageObjectFit, objectPosition: config.homepageObjectPosition }}
      fill
    />
  );

  return (
    <PromoCard
      backgroundColor={config.backgroundColor}
      eyebrowSlot={<EndsSoonEyebrow variant={variant} />}
      id={cardId}
      backgroundImageSlot={
        config.homepageBgMobile ? (
          <>
            <Box
              bottom={0}
              display={{ base: 'block', md: 'none' }}
              left={0}
              pointerEvents='none'
              position='absolute'
              right={0}
              top={0}
              zIndex={0}
            >
              <Image
                alt=''
                loading='eager'
                priority={imagePriority}
                sizes='100vw'
                src={config.homepageBgMobile}
                style={{
                  objectFit: config.homepageMobileObjectFit ?? config.homepageObjectFit,
                  objectPosition: config.homepageMobileObjectPosition ?? config.homepageObjectPosition,
                }}
                fill
              />
            </Box>
            <Box
              bottom={0}
              display={{ base: 'none', md: 'block' }}
              left={0}
              pointerEvents='none'
              position='absolute'
              right={0}
              top={0}
              zIndex={0}
            >
              {backgroundImage}
            </Box>
          </>
        ) : (
          <Box bottom={0} left={0} pointerEvents='none' position='absolute' right={0} top={0} zIndex={0}>
            {backgroundImage}
          </Box>
        )
      }
      buttonSlot={
        <ButtonLink
          linkProps={{ href: checkoutHref }}
          buttonProps={{
            ...(config.homepageButtonHasRocketIcon
              ? {
                  size: 'icon-left' as const,
                  leftIcon: <RocketIcon height='18px' width='18px' />,
                }
              : {
                  backgroundColor: config.ctaColor,
                  size: 'md' as const,
                }),
            variant: 'solid',
            colorScheme: 'blue',
            position: 'relative',
            zIndex: 1,
            width: {
              base: '100%',
              md: '152px',
            },
            onClick,
          }}
        >
          {SUMMER_2026_SALE_CTA_LABEL}
        </ButtonLink>
      }
      contentSlot={
        <Box>
          <Heading
            as='h2'
            color={config.homepageTitleColor}
            fontWeight='600'
            id={cardId}
            letterSpacing='-0.01em'
            maxWidth={variant === 'summer' || variant === 'mid_year_sale' ? { base: '168px', md: 'unset' } : undefined}
            mb={3}
            fontSize={{
              base: '1.75rem',
              md: '2.25rem',
            }}
            lineHeight={{
              base: '1.14',
              md: '1.1',
            }}
          >
            <LinkOverlay href={checkoutHref} onClick={onClick}>
              {`${config.headlineLine1} ${config.headlineLine2}`}
            </LinkOverlay>
          </Heading>
          <Text
            color={config.subtitleColor}
            fontSize={variant === 'summer' || variant === 'mid_year_sale' ? 'md' : undefined}
            lineHeight={1.5}
          >
            {config.subtitle}
          </Text>
        </Box>
      }
      padding={{
        base: '32px',
        md: '48px 48px 56px 48px',
      }}
    />
  );
}
